/**
 * Auth Service — Microsoft Entra ID via MSAL Browser
 *
 * Handles login/logout and token acquisition for the Backend API.
 * The Client authenticates directly with Entra ID (SPA flow),
 * then passes the access token to the Backend for validation.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

/** Read a config value: runtime window._env_ takes precedence over Vite build-time env. */
const env = (key) => window._env_?.[key] || import.meta.env[key] || '';

/** True when no Entra client ID is configured (dev mode without Azure). */
export const devMode = !env('VITE_AZURE_CLIENT_ID');

const msalConfig = {
  auth: {
    clientId: env('VITE_AZURE_CLIENT_ID'),
    authority: `https://login.microsoftonline.com/${env('VITE_AZURE_TENANT_ID') || 'common'}`,
    redirectUri: env('VITE_AZURE_REDIRECT_URI') || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// Scopes: the Backend's API audience. Adjust if you registered a custom scope.
const loginScopes = {
  scopes: [`api://${env('VITE_AZURE_CLIENT_ID')}/access_as_user`],
};

let msalInstance = null;
let initPromise = null;

/**
 * Initialize MSAL. Safe to call multiple times — returns cached promise.
 */
export function initMsal() {
  if (devMode) return Promise.resolve(null);
  if (initPromise) return initPromise;
  initPromise = (async () => {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    // Handle redirect response (back from Entra ID login page)
    await msalInstance.handleRedirectPromise();
    return msalInstance;
  })();
  return initPromise;
}

/**
 * Get the current MSAL instance (must call initMsal first).
 */
export function getMsalInstance() {
  return msalInstance;
}

/**
 * Get the currently signed-in account, or null.
 */
export function getAccount() {
  if (!msalInstance) return null;
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

/**
 * Acquire an access token silently, falling back to redirect if needed.
 * Returns the access token string, or null if not authenticated.
 */
export async function getAccessToken() {
  if (devMode) return null;
  await initMsal();
  const account = getAccount();
  if (!account) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginScopes,
      account,
    });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Token expired or consent needed — redirect to login
      await msalInstance.acquireTokenRedirect(loginScopes);
      return null; // won't reach here, page redirects
    }
    console.error('[auth] acquireTokenSilent failed:', err);
    return null;
  }
}

/**
 * Sign in via redirect (full-page navigation to Entra ID).
 */
export async function login() {
  if (devMode) { console.warn('[auth] login skipped — no VITE_AZURE_CLIENT_ID'); return; }
  await initMsal();
  await msalInstance.loginRedirect(loginScopes);
}

/**
 * Sign out and redirect to the app's origin.
 */
export async function logout() {
  if (devMode) { console.warn('[auth] logout skipped — no VITE_AZURE_CLIENT_ID'); return; }
  await initMsal();
  const account = getAccount();
  await msalInstance.logoutRedirect({ account });
}

/**
 * Check if a user is currently authenticated.
 */
export function isAuthenticated() {
  return getAccount() !== null;
}
